import React, {
  createContext,
  useState,
  useEffect,
  useContext,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { devLog, devError } from "@/utils/devLog";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  initialSessionProcessed: boolean;
  login: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialSessionProcessed, setInitialSessionProcessed] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    console.log("[AuthProvider] ➡️ Initializing");
    
    // 1. Get existing session first
    supabase.auth.getSession().then(({ data, error }) => {
      if (error) {
        devError("[AuthProvider] Error getting session:", error);
      } else {
        devLog("[AuthProvider] Initial session retrieved", {
          hasSession: !!data.session,
          userId: data.session?.user?.id
        });
        setSession(data.session);
        setUser(data.session?.user ?? null);
      }
      setReady(true);
      setInitialSessionProcessed(true);
    });
    
    // 2. Listen for future changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      devLog("[AuthProvider] Auth state changed:", { 
        event,
        hasSession: !!newSession,
        userId: newSession?.user?.id
      });
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setSession(newSession);
        setUser(newSession?.user ?? null);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
      }
    });
    
    return () => {
      devLog("[AuthProvider] Cleaning up auth subscription");
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string) => {
    try {
      setLoading(true);
      await supabase.auth.signInWithOtp({ email });
      alert("Check your email for the login link!");
    } catch (error) {
      console.error("Login error:", error);
      alert("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      // Redirect to home page after successful logout
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
      alert("Logout failed. Please try again.");
    }
  };

  // Don't render anything until we've checked for an existing session
  if (!ready) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        initialSessionProcessed,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
